import { Component, DestroyRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { NgClass, DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProductTplService } from '@app/sales/product-tpl/productTpl.service';
import { ProductsService } from '@app/sales/products/products.service';
import { QuotationService } from '../quotation.service';
import { AlertService } from '@app/shared/alert.service';
import { SalesOrderState, salesOrderColors, getOrderColor, getOrderLabel } from "@app/shared/constants";


@Component({
    selector: 'app-salehistory',
    imports: [DatePipe, RouterLink, FormatCurrencyPipe, MatButtonModule, MatIconModule, MatProgressSpinnerModule, NgClass],
    templateUrl: './salehistory.component.html',
    styleUrl: './salehistory.component.scss',
		changeDetection: ChangeDetectionStrategy.Eager,
		standalone: true,
})
export class SalehistoryComponent implements OnChanges, OnDestroy {

	public SalesOrderState = SalesOrderState;
  public salesOrderColors = salesOrderColors;
  public getColor = getOrderColor;
  public getLabel = getOrderLabel;

	@Input() customer: any;
	@Input() id: number = 0;
	@Input() openInNewTab: boolean = false;

	orders: Array<any> = [];
	displayOrders: Array<any> = [];
	loading = false;

	private readonly MAX_LINES = 60;

	private destroyRef = inject(DestroyRef);
	// (client, pièce courante) : chaque loadOrder repasse un objet client neuf, la requête ne repart que si l'un des deux change.
	private readonly query$ = new Subject<{ customerId: number; orderId: number }>();

	constructor(private quotationservice: QuotationService , private productsService: ProductsService ,  private alertService: AlertService) {
		// switchMap : la réponse d'un client quitté ne peut plus remplacer celle du client affiché.
		this.query$.pipe(
			distinctUntilChanged((a, b) => a.customerId === b.customerId && a.orderId === b.orderId),
			switchMap(({ customerId, orderId }) => {
				this.loading = !!customerId;
				if (!customerId) return of([]);
				return this.quotationservice.getCustomerHistory(customerId, orderId).pipe(
					map((orders: any) => orders ?? []),
					catchError((e: any) => {
						console.error('[SalehistoryComponent] historique client illisible', e);
						this.alertService.error("Historique du client illisible");
						return of([]);
					}),
				);
			}),
			takeUntilDestroyed(this.destroyRef),
		).subscribe((orders: Array<any>) => {
			this.loading = false;
			this.orders = orders;
			this.displayOrders = this.truncateOrders(this.orders);
		});
	}

	// Composant créé à l'ouverture de l'onglet « Historique » (matTabContent) : la requête coûteuse ne part pas avant.
	ngOnChanges(changes: SimpleChanges) {
		this.query$.next({ customerId: +this.customer?.id || 0, orderId: +this.id || 0 });
	}

	private truncateOrders(orders: Array<any>): Array<any> {
		const result: Array<any> = [];
		let total = 0;
		console.log('orders',orders);
		for (const order of orders) {
			if (!order || !order.lines || !order.lines.length) continue;
			// La plus récente passe toujours, même au-delà du plafond : sinon une pièce de 70 lignes vidait tout l'historique.
			if (result.length && total + order.lines.length > this.MAX_LINES) break;
			result.push(order);
			total += order.lines.length;
		}
		return result;
	}
	
	addproduct(product: any, qty: number = 1): void {
		console.log('this.customer',this.customer);
		console.log('this.customer?.data?.pricelists',this.customer?.data?.pricelists);
		// Rejouer une ligne d'historique = LE PRIX D'AUJOURD'HUI, LE CONDITIONNEMENT D'ALORS.
		//
		// Deux sources, chacune pour ce qu'elle sait :
		//   * le PRIX vient du serveur, par `jpid:{produit}`. /search/productpackaging
		//     est le seul point de l'API qui calcule le tarif client (il lit
		//     jpp:{produit}:{tarif}), d'où l'appel plutôt qu'une reconstruction
		//     locale — c'est ce que faisait l'implémentation commentée plus bas,
		//     au prix de rejouer l'ANCIEN prix.
		//   * le CONDITIONNEMENT vient de la LIGNE, qui le porte dénormalisé
		//     (ppv, packaging_qtybaseuom, packaging_name — colonnes de
		//     sales_order_l). Sans cette reprise, rejouer un « carton de 100 »
		//     ramènerait le conditionnement PAR DÉFAUT du produit : addLine pose
		//     amount = net × qtybaseuom, donc une ligne divisée par 100, en
		//     silence. C'est le seul dommage réel du passage par le produit, et
		//     il s'évite par la ligne — pas par un index.
		//
		// Sur l'index : une famille jppv:{id} a existé le temps de la bascule et a
		// été retirée le 28/08/2026. Elle n'aurait rien apporté ici — aucun champ
		// de prix n'est propre au conditionnement — et lire par clé primaire n'est
		// pas chercher.
		//
		// La garde porte sur `product` (NOT NULL en base) et non plus sur `ppv` :
		// le bouton « + » est donc opérant sur TOUTES les lignes, y compris les
		// 1,4 M de lignes reprises sans conditionnement. Sur celles-là, le
		// conditionnement rejoué est le défaut du produit — faute de mieux, et
		// c'est déjà mieux que le no-op silencieux d'avant.
		if(product.product){
			// takeUntilDestroyed remplace le `subscription.unsubscribe()` posé dans le `next` : celui-ci
			// ne coupait QUE la souscription déjà consommée, jamais la requête EN VOL. Or la réponse
			// arrive un aller-retour réseau plus tard et écrit dans QuotationService.subject, un
			// service RACINE : si l'écran a été remis à zéro entre-temps (F1, « Vente Comptoir »), la
			// ligne de l'ANCIENNE vente atterrissait toute seule dans le ticket neuf. Le drapeau
			// `destroyed` ne pouvait rien contre ça — il n'arrête que la chaîne de minuteries.
			this.productsService.search_productpackaging('jpid', product.product , this.customer?.data?.pricelists)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
				next: (ppv: any) => {
					if (ppv) {
						const ppv_rejoue = {
							...ppv,
							// L'id du conditionnement RÉELLEMENT vendu, pour que la ligne
							// rejouée pointe le même que l'originale. Sur une ligne reprise
							// (ppv NULL) on retombe sur le défaut rendu par jpid:.
							id: product.ppv ?? ppv.id,
							// Le colisage d'alors : c'est lui qui fait le montant.
							qtybaseuom: product.packaging_qtybaseuom ?? ppv.qtybaseuom,
							// Libellé figé à la vente ; addLine le préfère à son calcul.
							packaging_name: product.packaging_name,
						};
						this.quotationservice.sendAddproductpackagingfromHistory(ppv_rejoue, qty);
					} else { // Le produit a disparu du catalogue, ou n'a aucun conditionnement par défaut
          				this.alertService.warn(" Produit issu de l'historique non trouvé au catalogue : " + product.product_name);
        			}
				}, error: (e) => { this.alertService.error(" Erreur serveur api  : " + e); console.error("ERREUR Serveur : ", e.error);}
			});
		}

		// let ppv = {
		// 	product: product.product,
		// 	barcode: product.product_barcode,
		// 	lnk_product: {
		// 		template: product.template,
		// 		name: product.product_name,
		// 		price_taxi: product.uprice_taxi,
		// 		price_taxx: product.uprice_taxx,
		// 		lnk_template: { vat: +product.vat, vat_rate: product.vat_rate }
		// 	},
		// 	lnk_packaging: { qty: product.packaging_qtybaseuom }
		// }
		// // product.lnk_product.template = product.template
		// this.quotationservice.sendAddproductpackagingfromHistory(ppv, qty);

	}

	// Cette boucle pousse des produits dans QuotationService.subject, qui est un service RACINE :
	// ce qu'elle envoie atterrit dans la commande affichée à cet instant-là, pas dans celle qui
	// était ouverte au moment du clic. Sur une reprise de vente de 20 lignes, elle court pendant
	// près de quatre secondes — largement de quoi laisser à l'utilisateur le temps de repartir à
	// zéro (menu « Vente Comptoir », F1) et de voir l'ancienne vente se déverser dans la nouvelle.
	// Le drapeau coupe la chaîne de MINUTERIES ; les requêtes déjà parties, elles, sont coupées
	// par le takeUntilDestroyed d'addproduct. Il faut les deux : ni l'un ni l'autre ne suffit.
	private destroyed = false;

	ngOnDestroy(): void {
		this.destroyed = true;
	}

	async addAllproductOfOrder(lines: Array<any>): Promise<void> {
		for (const line of lines) {
			if (this.destroyed) return;
			this.addproduct(line, line.qty);
			await new Promise(resolve => setTimeout(resolve, 190)); // le timer de 120ms entre chaque appel permet de faire un effet d'avancement (c'est joli ) mais surtout si un appel api répond avant un autre les article ne sont pas dans le même ordre ce qui peut être pertubant
		}
	}


}
